import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useCuringEstimate } from '../../api/curing';
import { useWeatherStatistics } from '../../api/weatherHistory';
import DroughtIndexControl from './DroughtIndexControl';
import {
  Box,
  Typography,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Button,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  PlayArrow as PlayArrowIcon,
  Warning as WarningIcon,
  Whatshot as WhatshotIcon,
  Thermostat as ThermostatIcon,
  WaterDrop as WaterDropIcon,
  LocalFireDepartment as FireIcon,
} from '@mui/icons-material';
import {
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  ReferenceDot,
} from 'recharts';
import dayjs from 'dayjs';

import {
  useSeasonalForecast,
  fetchSeasonalWindow,
  SeasonalLensKey,
} from '../../api/seasonalForecast';
import { useUnitSystem } from '../../hooks/useUnitSystem';
import { useTimezone } from '../../contexts/TimezoneContext';
import { useSimulation, SimulationParams } from '../../contexts/SimulationContext';
import { IgnitionRegimeContext } from '../../contexts/IgnitionRegimeContext';
import { useAuth } from '../../contexts/AuthContext';
import { toDisplayTemperature, toDisplaySpeed, formatNumber } from '../../utils/units';
import { getDangerCategory, FDI_THRESHOLDS } from '../../utils/fireDanger';
import { transposeWeatherTimestamps } from '../../utils/datetime';
import SpottingModeSelector from './SpottingModeSelector';
import { SpottingMode } from '../../types/spotting';

interface SeasonalOutlookViewProps {
  projectId: string;
  onGenerate?: (params?: SimulationParams) => void;
  onClose?: () => void;
  weatherDate: dayjs.Dayjs;
  projectDate: dayjs.Dayjs;
}

// Scenario lens = a percentile across the 50 members, taken at each day.
// Stops: deciles P10–P90, plus a P99 extreme-tail stop.
const PCT_STOPS = [10, 20, 30, 40, 50, 60, 70, 80, 90, 99];
const lensKey = (pct: number) => `p${pct}`;
const pctOf = (key: string) => parseInt(key.slice(1), 10);
const ordinal = (n: number) => {
  const s = ['th', 'st', 'nd', 'rd'], v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};
const lensLabel = (key: string) => `${ordinal(pctOf(key))} percentile`;
const lensMeaning = (pct: number) => {
  if (pct === 50) return 'a median (typical) season';
  if (pct < 50) return 'a milder-than-typical season';
  return `roughly a 1-in-${Math.round(100 / (100 - pct))} bad season`;
};

/** Cell colour for the weekly exceedance ribbon — cool→hot ramp by probability. */
const RIBBON_STOPS: [number, [number, number, number]][] = [
  [0, [43, 49, 71]], [0.25, [255, 215, 0]], [0.5, [255, 152, 0]], [0.75, [255, 107, 53]], [1, [211, 47, 47]],
];
const RIBBON_GRADIENT = 'linear-gradient(90deg, rgb(43,49,71), #ffd700, #ff9800, #ff6b35, #d32f2f)';
const ribbonColor = (prob: number) => {
  for (let i = 1; i < RIBBON_STOPS.length; i++) {
    if (prob <= RIBBON_STOPS[i][0]) {
      const [t0, c0] = RIBBON_STOPS[i - 1];
      const [t1, c1] = RIBBON_STOPS[i];
      const f = (prob - t0) / (t1 - t0 || 1);
      const c = c0.map((v, j) => Math.round(v + (c1[j] - v) * f));
      return `rgb(${c[0]}, ${c[1]}, ${c[2]})`;
    }
  }
  return 'rgb(211,47,47)';
};

const panelSx = { bgcolor: 'rgba(255, 255, 255, 0.05)', borderRadius: 2, p: 3, mb: 3 };

/** Colour for the monthly "outlook vs normal" classification. */
const OUTLOOK_COLOR: Record<string, string> = {
  'Below': '#64b5f6',
  'Near normal': '#9e9e9e',
  'Above': '#ff9800',
  'Well above': '#d32f2f',
};

const SeasonalOutlookView: React.FC<SeasonalOutlookViewProps> = ({
  projectId,
  onGenerate,
  onClose,
  // weatherDate is accepted for parity with the other Action views, but the
  // ignition window is keyed off the selected forecast day instead.
  projectDate,
}) => {
  const { data: summary, isLoading } = useSeasonalForecast(projectId);

  // The seasonal view's dates sit months out, beyond the two-week window a
  // satellite reading is held for, so we only use the endpoint's climatology
  // suggestion here -- never a stale measurement presented as current.
  const { data: curingData } = useCuringEstimate(projectId);
  const suggestedCuring = curingData?.for_date?.suggested ?? null;

  useEffect(() => {
    if (suggestedCuring !== null && suggestedCuring !== undefined) {
      setCuring(suggestedCuring);
    }
  }, [suggestedCuring]);
  const unitSystem = useUnitSystem();
  const { selectedTimezone } = useTimezone();
  const { simulationParams, setSimulationParams } = useSimulation();
  const { selectedRegime } = useContext(IgnitionRegimeContext);
  const { user } = useAuth();

  const hasLongRange =
    user?.is_staff ||
    user?.capabilities?.some((cap) => cap.codename === 'longrange_simulation');
  const maxDuration = hasLongRange ? 336 : 48;

  const [lens, setLens] = useState<SeasonalLensKey>('p90');
  const [selectedDay, setSelectedDay] = useState(0);
  const [startHour, setStartHour] = useState(10);
  const [duration, setDuration] = useState(8);
  const [curing, setCuring] = useState(100);
  // Months out, no forecast carries a drought index; start from the latest
  // value the project's history holds and let the user move it.
  const { data: projectWeatherStats } = useWeatherStatistics(projectId);
  const [droughtIndex, setDroughtIndex] = useState<number | null>(null);
  useEffect(() => {
    if (droughtIndex === null && projectWeatherStats?.kbdi?.latest != null) {
      setDroughtIndex(projectWeatherStats.kbdi.latest);
    }
  }, [projectWeatherStats, droughtIndex]);
  const [spottingMode, setSpottingMode] = useState<SpottingMode>(simulationParams?.spottingMode ?? 'realistic');
  const [isGenerating, setIsGenerating] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>(
    { open: false, message: '', severity: 'success' }
  );

  const ready = summary?.status === 'ready' && !!summary.lenses && !!summary.bands && !!summary.days;
  const currentLens = ready ? summary!.lenses![lens] : undefined;

  // Default the ignition window to the selected season's worst day.
  useEffect(() => {
    if (currentLens) setSelectedDay(currentLens.stats.peak_day);
  }, [lens, summary?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const tempUnit = unitSystem === 'imperial' ? '°F' : '°C';
  const windUnit = unitSystem === 'imperial' ? 'mph' : 'km/h';
  const showTemp = (c: number) => formatNumber(toDisplayTemperature(c, unitSystem), { maximumFractionDigits: 0 });
  const showWind = (k: number) => formatNumber(toDisplaySpeed(k, unitSystem), { maximumFractionDigits: 0 });

  const days = useMemo(() => summary?.days ?? [], [summary]);
  const bands = summary?.bands;

  const chartData = useMemo(() => {
    if (!ready || !bands || !currentLens) return [];
    const spag = summary!.spaghetti ?? [];
    return days.map((d, i) => {
      const point: Record<string, number | number[] | string | null> = {
        label: d,
        tail: [bands.p90[i], bands.max[i]],
        band_outer: [bands.p10[i], bands.p90[i]],
        band_mid: [bands.p50[i], bands.p90[i]],
        band_inner: [bands.p25[i], bands.p75[i]],
        p50: bands.p50[i],
        localMedian: summary!.local_median?.[i] ?? null,
        trace: currentLens.daily_peak[i],
      };
      spag.slice(0, 10).forEach((series, si) => {
        point[`sg${si}`] = series[i];
      });
      return point;
    });
  }, [ready, bands, currentLens, days, summary]);

  const yMax = useMemo(() => {
    // Scale to the SELECTED lens's peak (not the worst), so the axis adapts to
    // whichever season you're viewing. Background bands/outliers clip at the top.
    const robust = currentLens?.stats.peak ?? 40;
    const withHead = robust * 1.3;
    const step = withHead > 120 ? 25 : withHead > 45 ? 10 : 5;
    return Math.min(200, Math.max(10, Math.ceil(withHead / step) * step));
  }, [currentLens]);

  // First-of-month X-axis ticks.
  const monthTicks = useMemo(() => {
    const ticks: string[] = [];
    let lastMonth = '';
    days.forEach((d) => {
      const m = d.slice(0, 7);
      if (m !== lastMonth) {
        ticks.push(d);
        lastMonth = m;
      }
    });
    return ticks;
  }, [days]);

  if (isLoading || !ready) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 420, gap: 2 }}>
        <CircularProgress sx={{ color: '#ff6b35' }} />
        <Typography sx={{ color: 'rgba(255,255,255,0.6)' }}>
          Preparing seasonal outlook… this can take a minute the first time.
        </Typography>
      </Box>
    );
  }

  const summ = summary!;
  const lensObj = currentLens!;
  const stats = lensObj.stats;
  const typicalStats = summ.lenses!['p50'].stats;
  const peakCat = getDangerCategory(stats.peak);
  const typicalPeakCat = getDangerCategory(typicalStats.peak);
  const M = summ.provenance?.members ?? 0;

  const startEnd = days.length
    ? `${dayjs(days[0]).format('MMM')} – ${dayjs(days[days.length - 1]).format('MMM YYYY')}`
    : '';

  const cond = lensObj.day_conditions[selectedDay];
  const selectedLabel = days[selectedDay];

  const cards: {
    color: string;
    icon: React.ReactNode;
    label: string;
    value: string;
    unit: string;
    sub?: string;
    subColor?: string;
    foot: string;
  }[] = [
    {
      color: peakCat.color,
      icon: <WhatshotIcon sx={{ color: peakCat.color, fontSize: 18 }} />,
      label: 'Peak fire danger',
      value: `${Math.round(stats.peak)}`,
      unit: 'FFDI',
      sub: peakCat.label,
      subColor: peakCat.color,
      foot: `Typical year peaks ${typicalPeakCat.label} (${Math.round(typicalStats.peak)})`,
    },
    {
      color: '#ff6b35',
      icon: <FireIcon sx={{ color: '#ff6b35', fontSize: 18 }} />,
      label: 'Days as severe as local worst-third',
      value: `${stats.high_days ?? 0}`,
      unit: 'days',
      foot: `forecast days above this site's 67th-percentile FFDI · ${stats.record ?? 0} beat the local record`,
    },
    {
      color: '#f44336',
      icon: <ThermostatIcon sx={{ color: '#f44336', fontSize: 18 }} />,
      label: 'Hottest day',
      value: showTemp(stats.hottest),
      unit: tempUnit,
      foot: `${stats.over40} days over ${showTemp(40)}${tempUnit}`,
    },
    {
      color: '#a1887f',
      icon: <WaterDropIcon sx={{ color: '#a1887f', fontSize: 18 }} />,
      label: 'Longest dry spell',
      value: `${stats.dry}`,
      unit: 'days',
      foot: `drought factor ${stats.df.toFixed(1)} / 10`,
    },
  ];

  const handleChartClick = (e: { activeTooltipIndex?: number } | null) => {
    if (e && e.activeTooltipIndex != null) setSelectedDay(e.activeTooltipIndex);
  };

  const handleWeekClick = (weekIdx: number) => {
    const a = weekIdx * 7;
    const b = Math.min(days.length, a + 7);
    let best = a;
    for (let d = a; d < b; d++) {
      if (lensObj.daily_peak[d] > lensObj.daily_peak[best]) best = d;
    }
    setSelectedDay(best);
  };

  const handleGenerate = async () => {
    if (!simulationParams) return;
    setIsGenerating(true);
    try {
      const dayStr = days[selectedDay];
      const startTs = `${dayStr}T${String(startHour).padStart(2, '0')}:00`;
      const res = await fetchSeasonalWindow(projectId, lens, startTs, duration);
      const win = res.data;
      if (!win?.interval_weather) throw new Error('Forecast window not ready');

      // Align the forecast window onto the project's landscape date.
      const transposed = transposeWeatherTimestamps(
        win.interval_weather,
        dayjs(dayStr),
        projectDate
      );

      const simulationTime = dayjs(projectDate).hour(startHour).minute(0).second(0).millisecond(0);
      const simulationTimeTz = simulationTime.tz(selectedTimezone, true);

      const nextParams: SimulationParams = {
        ...simulationParams,
        simulationType: 'USE_WEATHER_FORECAST',
        droughtFactor: win.drought_factor,
        // Seasonal windows carry a drought factor but no drought index; the
        // user's setting (seeded from the project's latest value) goes instead.
        droughtIndex,
        curing,
        interval: (duration * 60) / 240,
        duration,
        startTime: simulationTimeTz.format('YYYY-MM-DDTHH:mm:ss.SSSZ'),
        timezone: selectedTimezone,
        weather: transposed || {},
        fireSpreadModel: 'New',
        spottingMode,
        ignitionRegimeId: selectedRegime?.id || null,
        weatherLabel: `Seasonal ${lensLabel(lens)} · ${dayjs(dayStr).format('MMM D')}`,
        // Seasonal windows carry no lattice; drop any grid left in context.
        weather_grid: undefined,
      };

      setSimulationParams(nextParams);

      // Pass the freshly-built params directly so the generated sim uses the
      // conditions just selected. Relying on the context state alone would race
      // the setSimulationParams update above and send the previous run's params.
      await onGenerate?.(nextParams);
      onClose?.();
    } catch {
      setSnackbar({ open: true, message: 'Could not start simulation from this forecast window.', severity: 'error' });
    } finally {
      setIsGenerating(false);
    }
  };

  const FanTooltip = ({
    active,
    payload,
    label,
  }: {
    active?: boolean;
    payload?: { dataKey?: string; value?: number | number[] }[];
    label?: string;
  }) => {
    if (!active || !payload?.length) return null;
    const get = (key: string) => payload.find((p) => p.dataKey === key)?.value;
    const num = (v: number | number[] | undefined) => (typeof v === 'number' ? v : 0);
    const range = get('band_outer');
    return (
      <Box sx={{ bgcolor: 'rgba(0,0,0,0.9)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 2, p: 1.5 }}>
        <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: 13, mb: 0.5 }}>
          {dayjs(label).format('ddd D MMM')}
        </Typography>
        <Typography sx={{ color: '#ff6b35', fontSize: 12 }}>
          {lensLabel(lens)}: FFDI {Math.round(num(get('trace')))}
        </Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: 12 }}>
          Median: FFDI {Math.round(num(get('p50')))}
        </Typography>
        {Array.isArray(range) && (
          <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 11 }}>
            10–90th pct: {Math.round(range[0])}–{Math.round(range[1])}
          </Typography>
        )}
      </Box>
    );
  };

  const weeks = summ.weekly_exceedance ?? [];
  const ignitionReady = !!selectedRegime?.id;

  return (
    <Box>
      {/* Header + scenario lens */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ color: '#fff', fontWeight: 600 }}>
            Seasonal Outlook — next 6 months
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
            {startEnd} · {M}-member ensemble · {summ.provenance?.resolution} · {summ.provenance?.model} via {summ.provenance?.source}
          </Typography>
        </Box>
        <Box sx={{ width: 320, maxWidth: '100%', mr: 5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', fontSize: '0.62rem', letterSpacing: 0.5 }}>
              Scenario percentile
            </Typography>
            <Typography variant="caption" sx={{ color: '#ff6b35', fontWeight: 700 }}>
              {ordinal(pctOf(lens))} pct
            </Typography>
          </Box>
          <Slider
            value={pctOf(lens)}
            onChange={(_, v) => setLens(lensKey(v as number))}
            step={null}
            min={10}
            max={99}
            marks={PCT_STOPS.map((p) => ({ value: p, label: p === 99 ? '99' : String(p) }))}
            sx={{
              color: '#ff6b35',
              '& .MuiSlider-markLabel': { color: 'rgba(255,255,255,0.4)', fontSize: '0.6rem' },
              '& .MuiSlider-mark': { bgcolor: 'rgba(255,255,255,0.3)' },
              '& .MuiSlider-rail': { bgcolor: 'rgba(255,255,255,0.15)' },
            }}
          />
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', display: 'block', textAlign: 'right' }}>
            {lensMeaning(pctOf(lens))}
          </Typography>
        </Box>
      </Box>

      {/* Framing */}
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', bgcolor: 'rgba(255,107,53,0.08)', border: '1px solid rgba(255,107,53,0.25)', borderLeft: '3px solid #ff6b35', borderRadius: 1, p: 1.5, mb: 3 }}>
        <WarningIcon sx={{ color: '#ff6b35', fontSize: 20, mt: '1px' }} />
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', lineHeight: 1.55 }}>
          <b style={{ color: '#fff' }}>Don't plan for the average.</b> Averaging {M} forecasts hides the dangerous outliers.
          Drag the <b style={{ color: '#fff' }}>scenario percentile</b> to walk from a typical (median) season up to a 1-in-100 extreme — at each setting the line is that percentile across the {M} members, taken day by day.
          Everything is measured against <b style={{ color: '#fff' }}>this location's own normal</b>, so "worst-33%" and "record" mean relative to here, not a fixed national scale.
        </Typography>
      </Box>

      {/* Hero cards */}
      <Grid container spacing={3} sx={{ mb: 1 }}>
        {cards.map((c) => (
          <Grid item xs={3} key={c.label}>
            <Card sx={{ bgcolor: `${c.color}1A`, border: `1px solid ${c.color}55`, height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  {c.icon}
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', fontSize: '0.68rem' }}>
                    {c.label}
                  </Typography>
                </Box>
                <Typography variant="h3" sx={{ color: '#fff', fontWeight: 600, lineHeight: 1.05 }}>
                  {c.value}
                  <span style={{ fontSize: '1rem', fontWeight: 500, color: 'rgba(255,255,255,0.7)', marginLeft: 3 }}>{c.unit}</span>
                </Typography>
                {c.sub && (
                  <Typography sx={{ color: c.subColor, fontWeight: 700, fontSize: '0.95rem' }}>{c.sub}</Typography>
                )}
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', display: 'block', mt: 0.5 }}>
                  {c.foot}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Fan chart */}
      <Box sx={panelSx}>
        <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600, mb: 0.5 }}>
          Fire Danger Index — daily peak across the season
        </Typography>
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', display: 'block', mb: 1 }}>
          Click any day on the chart (or a week on the ribbon below) to set the ignition window — then generate a fire simulation from that forecast.
        </Typography>
        {/* legend */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1.5 }}>
          {[
            { sw: 'linear-gradient(90deg,#ffb74d,#d32f2f)', label: `Selected season (${lensLabel(lens)})`, line: true },
            { sw: 'rgba(211,47,47,0.32)', label: 'Extreme tail (P90–max)' },
            { sw: 'rgba(255,152,0,0.30)', label: '50–90th pct' },
            { sw: 'rgba(255,152,0,0.14)', label: '10–90th pct' },
            { sw: 'rgba(255,255,255,0.42)', label: 'Forecast median', line: true },
            { sw: '#4dd0e1', label: 'Local normal (median)', line: true },
            { sw: 'rgba(255,255,255,0.12)', label: 'Members', line: true },
          ].map((l) => (
            <Box key={l.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box sx={{ width: 16, height: l.line ? 3 : 12, borderRadius: l.line ? 0 : 0.5, background: l.sw }} />
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>{l.label}</Typography>
            </Box>
          ))}
        </Box>
        <ResponsiveContainer width="100%" height={360}>
          <ComposedChart data={chartData} onClick={handleChartClick} margin={{ top: 10, right: 64, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
            <XAxis
              dataKey="label"
              ticks={monthTicks}
              tickFormatter={(d) => dayjs(d).format('MMM')}
              tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 11 }}
              stroke="rgba(255,255,255,0.3)"
            />
            <YAxis
              domain={[0, yMax]}
              tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 11 }}
              stroke="rgba(255,255,255,0.3)"
              width={40}
            />
            <Tooltip content={<FanTooltip />} />

            <defs>
              <linearGradient id="seasonalTrace" x1="0" y1="1" x2="0" y2="0">
                <stop offset="0" stopColor="#ffb74d" />
                <stop offset="0.45" stopColor="#ff6b35" />
                <stop offset="1" stopColor="#d32f2f" />
              </linearGradient>
            </defs>

            {/* extreme tail (P90 -> max) */}
            <Area dataKey="tail" stroke="none" fill="rgba(211,47,47,0.10)" isAnimationActive={false} />
            <Area dataKey="band_outer" stroke="none" fill="rgba(255,152,0,0.13)" isAnimationActive={false} />
            <Area dataKey="band_mid" stroke="none" fill="rgba(255,152,0,0.18)" isAnimationActive={false} />
            <Area dataKey="band_inner" stroke="none" fill="rgba(255,152,0,0.16)" isAnimationActive={false} />

            {/* faint ensemble members */}
            {(summ.spaghetti ?? []).slice(0, 10).map((_, si) => (
              <Line key={`sg${si}`} dataKey={`sg${si}`} stroke="rgba(255,255,255,0.06)" dot={false} strokeWidth={1} isAnimationActive={false} />
            ))}

            {/* median */}
            <Line dataKey="p50" stroke="rgba(255,255,255,0.42)" dot={false} strokeWidth={1.5} strokeDasharray="2 3" isAnimationActive={false} />

            {/* local "normal" line — this site's historical median FFDI by month */}
            <Line dataKey="localMedian" stroke="#4dd0e1" dot={false} strokeWidth={1.5} strokeDasharray="6 3" strokeOpacity={0.85} isAnimationActive={false} connectNulls />

            {/* danger thresholds (shared definition; only those within view) */}
            {FDI_THRESHOLDS.filter((t) => t.value <= yMax).map((t) => (
              <ReferenceLine key={t.label} y={t.value} stroke={t.color} strokeDasharray="5 4" strokeOpacity={0.85}
                label={{ value: t.label, fill: t.color, position: 'right', fontSize: 10 }} />
            ))}

            {/* selected-season trajectory — translucent halo layers + crisp gradient line */}
            <Line dataKey="trace" stroke="url(#seasonalTrace)" strokeWidth={8} strokeOpacity={0.16} dot={false} isAnimationActive={false} />
            <Line dataKey="trace" stroke="url(#seasonalTrace)" strokeWidth={4.5} strokeOpacity={0.34} dot={false} isAnimationActive={false} />
            <Line dataKey="trace" stroke="url(#seasonalTrace)" strokeWidth={2.6} dot={false} isAnimationActive={false} />

            {/* ignition window cursor + worst-day marker + callout */}
            {selectedLabel && <ReferenceLine x={selectedLabel} stroke="#ff6b35" strokeDasharray="4 3" strokeOpacity={0.85}
              label={{ value: 'Ignition window', fill: '#ff6b35', fontSize: 10, position: 'insideTopRight' }} />}
            <ReferenceDot
              x={days[stats.peak_day]}
              y={stats.peak}
              r={5}
              fill="#fff"
              stroke={peakCat.color}
              strokeWidth={2}
              label={{
                value: `${peakCat.label} · FFDI ${Math.round(stats.peak)} — worst day, wk ${dayjs(days[stats.peak_day]).format('D MMM')}`,
                position: stats.peak_day > days.length * 0.55 ? 'left' : 'right',
                fill: peakCat.color,
                fontSize: 11,
                fontWeight: 700,
              }}
            />
          </ComposedChart>
        </ResponsiveContainer>

        {/* weekly exceedance ribbon */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1.5, mb: 0.5 }}>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
            Chance of a <b style={{ color: '#fff' }}>worst-33% day</b> (top third for this site), by week
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>0%</Typography>
            <Box sx={{ width: 120, height: 8, borderRadius: 1, background: RIBBON_GRADIENT }} />
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>100%</Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: '2px' }}>
          {weeks.map((w) => (
            <Box
              key={w.week}
              onClick={() => handleWeekClick(w.week)}
              title={`${dayjs(w.start).format('D MMM')} — ${Math.round(w.prob * 100)}% chance of Extreme+`}
              sx={{
                flex: 1,
                height: 22,
                borderRadius: '2px',
                cursor: 'pointer',
                bgcolor: ribbonColor(w.prob),
                opacity: 0.45 + 0.55 * w.prob,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 9,
                color: '#fff',
                fontWeight: 600,
              }}
            >
              {w.prob >= 0.5 ? Math.round(w.prob * 100) : ''}
            </Box>
          ))}
        </Box>
        {/* month labels aligned to the ribbon */}
        <Box sx={{ display: 'flex', gap: '2px', mt: 0.5 }}>
          {weeks.map((w, i) => {
            const m = dayjs(w.start).format('MMM');
            const prev = i > 0 ? dayjs(weeks[i - 1].start).format('MMM') : '';
            return (
              <Box key={w.week} sx={{ flex: 1, fontSize: 9, color: 'rgba(255,255,255,0.4)' }}>
                {m !== prev ? m : ''}
              </Box>
            );
          })}
        </Box>
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', mt: 1.5, display: 'block' }}>
          The <b style={{ color: 'rgba(255,255,255,0.7)' }}>{ordinal(lensObj.percentile)} percentile</b> ({lensMeaning(lensObj.percentile)}) — taken across all {M} members each day,
          so the member at that percentile can differ day to day. It peaks <b style={{ color: peakCat.color }}>{peakCat.label} (FFDI {Math.round(stats.peak)})</b> in the week of <b>{dayjs(days[stats.peak_day]).format('D MMM')}</b>,
          with <b>{stats.high_days ?? 0} days in the worst 33% for this site</b>{(stats.record ?? 0) ? ` and ${stats.record} that beat the local record` : ''}. The cyan line is the local normal (median); the shaded fan is the per-day spread of all {M} members.
        </Typography>
      </Box>

      {/* Monthly breakdown */}
      <Box sx={panelSx}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600 }}>Month-by-month risk breakdown</Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>Peak & outlook span all {M} members · worst-third follows the selected scenario</Typography>
        </Box>
        <Table size="small">
          <TableHead>
            <TableRow>
              {['Month', 'Worst-member peak FFDI', `Worst-third days (${ordinal(lensObj.percentile)} pct)`, 'Outlook vs normal', 'Forecast vs local normal'].map((h, i) => (
                <TableCell key={h} align={i === 0 ? 'left' : i === 4 ? 'left' : 'right'}
                  sx={{ color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', fontSize: '0.65rem', borderColor: 'rgba(255,255,255,0.1)' }}>
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {(summ.monthly ?? []).map((m, i) => {
              const cat = getDangerCategory(m.worst_peak);
              const oc = OUTLOOK_COLOR[m.outlook] ?? 'rgba(255,255,255,0.4)';
              const anomalyPct = m.anomaly != null ? Math.round((m.anomaly - 1) * 100) : null;
              const hi = lensObj.monthly_high?.[i] ?? 0;
              const hiPct = m.n_days ? Math.round((hi / m.n_days) * 100) : 0;
              return (
                <TableRow key={`${m.year}-${m.month}`}>
                  <TableCell sx={{ color: '#fff', fontWeight: 600, borderColor: 'rgba(255,255,255,0.05)' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 4, height: 22, borderRadius: 1, bgcolor: cat.color }} />
                      {dayjs(`${m.year}-${String(m.month).padStart(2, '0')}-01`).format('MMMM')}
                    </Box>
                  </TableCell>
                  <TableCell align="right" sx={{ borderColor: 'rgba(255,255,255,0.05)' }}>
                    <Box component="span" sx={{ bgcolor: `${cat.color}2E`, color: cat.color, fontWeight: 700, fontSize: '0.7rem', px: 1, py: '2px', borderRadius: 999 }}>
                      {cat.label} {Math.round(m.worst_peak)}
                    </Box>
                  </TableCell>
                  <TableCell align="right" sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.05)' }}>
                    {hi} <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.74rem' }}>of {m.n_days} d</span>
                    <span style={{ color: hiPct >= 33 ? '#ff9800' : 'rgba(255,255,255,0.55)', fontWeight: 600, marginLeft: 6 }}>{hiPct}%</span>
                  </TableCell>
                  <TableCell align="right" sx={{ borderColor: 'rgba(255,255,255,0.05)' }}>
                    <Box component="span" sx={{ bgcolor: `${oc}2E`, color: oc, fontWeight: 700, fontSize: '0.7rem', px: 1, py: '2px', borderRadius: 999, whiteSpace: 'nowrap' }}>
                      {m.outlook}{anomalyPct != null ? ` ${anomalyPct >= 0 ? '+' : ''}${anomalyPct}%` : ''}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ borderColor: 'rgba(255,255,255,0.05)' }}>
                    {/* diverging bar: centre = local normal; right = above, left = below */}
                    <Box sx={{ position: 'relative', height: 8, maxWidth: 200, bgcolor: 'rgba(255,255,255,0.06)', borderRadius: 1 }}>
                      <Box sx={{ position: 'absolute', left: '50%', top: -2, bottom: -2, width: '1px', bgcolor: 'rgba(255,255,255,0.3)' }} />
                      {m.anomaly != null && (
                        <Box sx={{
                          position: 'absolute', top: 0, bottom: 0, borderRadius: 1,
                          ...(m.anomaly >= 1
                            ? { left: '50%', width: `${Math.min(m.anomaly - 1, 1) * 50}%`, bgcolor: oc }
                            : { right: '50%', width: `${Math.min(1 - m.anomaly, 1) * 50}%`, bgcolor: '#64b5f6' }),
                        }} />
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>

      {/* Simulation settings */}
      <Box sx={panelSx}>
        <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600, mb: 2 }}>Simulation settings</Typography>
        <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel sx={{ color: 'rgba(255,255,255,0.7)' }}>Start Time</InputLabel>
            <Select value={startHour} label="Start Time" onChange={(e: SelectChangeEvent<number>) => setStartHour(Number(e.target.value))}
              sx={{ color: '#fff', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' } }}>
              {Array.from({ length: 24 }, (_, i) => <MenuItem key={i} value={i}>{i.toString().padStart(2, '0')}:00</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel sx={{ color: 'rgba(255,255,255,0.7)' }}>Duration</InputLabel>
            <Select value={duration} label="Duration" onChange={(e: SelectChangeEvent<number>) => setDuration(Number(e.target.value))}
              sx={{ color: '#fff', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' } }}>
              {Array.from({ length: Math.floor((maxDuration - 8) / 2) + 1 }, (_, i) => 8 + i * 2).map((h) => (
                <MenuItem key={h} value={h}>{h} hrs {h > 48 ? `(${Math.floor(h / 24)}d ${h % 24}h)` : ''}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
              Curing: <b style={{ color: '#fff' }}>{curing}%</b>
            </Typography>
            <Slider value={curing} onChange={(_, v) => setCuring(v as number)} min={0} max={100} step={1}
              sx={{ color: '#4caf50', '& .MuiSlider-rail': { bgcolor: 'rgba(255,255,255,0.1)' } }} />
            {/* Seasonal runs look months ahead, far past any satellite
                reading, so this is always the user's call. Start from what
                the project's own history suggests for that time of year. */}
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)', display: 'block' }}>
              {suggestedCuring !== null
                ? `Typically around ${suggestedCuring}% at this time of year here. No satellite reading reaches this far ahead.`
                : 'No satellite reading reaches this far ahead \u2014 set the curing you expect.'}
            </Typography>
          </Box>
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <DroughtIndexControl
              dense
              value={droughtIndex}
              onChange={setDroughtIndex}
              unitSystem={unitSystem}
              source={projectWeatherStats?.kbdi?.date
                ? `Today's value for this project (as of ${projectWeatherStats.kbdi.date}). No forecast reaches this far ahead, so set what you expect.`
                : null}
            />
          </Box>
          <Box sx={{ minWidth: 260 }}>
            <SpottingModeSelector value={spottingMode} onChange={setSpottingMode} dense />
          </Box>
        </Box>
      </Box>

      {/* Methodology */}
      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', display: 'block', lineHeight: 1.7, borderTop: '1px solid rgba(255,255,255,0.1)', pt: 1.5, mb: 10 }}>
        <b style={{ color: 'rgba(255,255,255,0.6)' }}>Methodology.</b> Source: {summ.provenance?.source} ({summ.provenance?.model}),
        {' '}{M}-member ensemble, daily aggregates over {summ.provenance?.horizon_days} days. Each member's daily max temperature, min humidity, max wind and
        drought factor give a daily peak FFDI (McArthur). The shaded fan is the per-day percentile envelope across all members; the bold line is the selected
        percentile (deciles P10–P90, plus a P99 extreme tail) across all members taken at each day — every day's value comes from the member sitting at that
        percentile, so a simulation seeded from a day uses that member's real weather. Local climatology (per month): the <b style={{ color: 'rgba(255,255,255,0.6)' }}>normal</b> line is this site's historical
        median FFDI; a <b style={{ color: 'rgba(255,255,255,0.6)' }}>worst-33%</b> day exceeds its historical 67th percentile and a <b style={{ color: 'rgba(255,255,255,0.6)' }}>record</b> day
        exceeds its historical max (baseline: {summ.provenance?.baseline}). Drought factor source: {summ.provenance?.drought_source}.
      </Typography>

      {/* Sticky generate bar */}
      <Box sx={{
        position: 'sticky', bottom: 0, mx: -3, mt: 2, px: 3, py: 1.5,
        display: 'flex', alignItems: 'center', gap: 2.5, flexWrap: 'wrap',
        bgcolor: '#141828', borderTop: '1px solid rgba(255,107,53,0.4)', boxShadow: '0 -14px 26px rgba(0,0,0,0.4)',
      }}>
        <Box sx={{ minWidth: 150 }}>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', fontSize: '0.6rem' }}>Ignition window</Typography>
          <Typography sx={{ color: '#fff', fontSize: '0.95rem' }}>
            <b style={{ color: '#ff6b35' }}>{lensLabel(lens)}</b> · {selectedLabel ? dayjs(selectedLabel).format('D MMM') : '—'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', flex: 1 }}>
          {cond && [
            `${showTemp(cond.t)}${tempUnit}`,
            `RH ${Math.round(cond.rh)}%`,
            `Wind ${showWind(cond.wind)} ${windUnit}${cond.dir != null ? ` @ ${Math.round(cond.dir)}°` : ''}`,
            `Drought ${cond.df.toFixed(1)}`,
            `FFDI ${Math.round(cond.ffdi)} · ${getDangerCategory(cond.ffdi).label}`,
          ].map((chip, i) => (
            <Box key={i} sx={{ fontSize: '0.74rem', color: 'rgba(255,255,255,0.8)', bgcolor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 1.5, px: 1.2, py: 0.5, whiteSpace: 'nowrap' }}>
              {chip}
            </Box>
          ))}
        </Box>
        <Button
          variant="contained"
          startIcon={isGenerating ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : <PlayArrowIcon />}
          onClick={handleGenerate}
          disabled={isGenerating || !ignitionReady}
          title={ignitionReady ? '' : 'Select an ignition regime with at least one ignition point first'}
          sx={{ bgcolor: '#ff6b35', fontWeight: 600, '&:hover': { bgcolor: '#ff7f50' }, '&:disabled': { bgcolor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.3)' } }}
        >
          {isGenerating ? 'Generating…' : 'Generate simulation'}
        </Button>
      </Box>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SeasonalOutlookView;
